import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { UserGroup } from '../../types';
import type { InterventionInput } from '../../types';
import { 
  useGetInterventionInputsQuery,
  useAddInterventionInputMutation,
  useUpdateInterventionInputMutation,
  useDeleteInterventionInputMutation
} from '../../store/is/interventionInputApi';
import AddEditInterventionInput from './AddEditInterventionInput';
import ViewInterventionInput from './ViewInterventionInput';
import InterventionInputImport from './InterventionInputImport';
import CustomerCodeMappingImport from './CustomerCodeMappingImport';
import InterventionInputTable from './InterventionInputTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import { Button } from '../ui/button'
import { FileUpIcon, PlusIcon } from 'lucide-react'

const InterventionInputs: React.FC = (): JSX.Element => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedInput, setSelectedInput] = useState<InterventionInput | null>(null);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isImportMappingsVisible, setIsImportMappingsVisible] = useState(false);
  const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);

  // RTK Query hooks
  const { data: apiInputs = [], refetch } = useGetInterventionInputsQuery();
  const [addInput] = useAddInterventionInputMutation();
  const [updateInput] = useUpdateInterventionInputMutation();
  const [deleteInput] = useDeleteInterventionInputMutation();

  const userGroups = useSelector((state: any) => 
    state.auth.groups?.map((group: any) => group?.name)
  ) || [];

  const hasEditPermission = userGroups.some(
    (group: string) => group === UserGroup.PROBA_OPERATOR || group === UserGroup.ADMIN
  );

  const handleAddEdit = async (values: Partial<InterventionInput>) => {
    try {
      if (selectedInput) {
        await updateInput({ 
          id: selectedInput._id, 
          body: values 
        }).unwrap();
        message.success('Input updated successfully');
      } else {
        await addInput(values).unwrap();
        message.success('Input added successfully');
      }
      refetch();
      
    } catch (err) {
      message.error('Operation failed. Please try again.');
    }

    setIsDrawerVisible(false);
    setSelectedInput(null);
  };

  const handleRowClick = (input: InterventionInput) => {
    setSelectedInput(input);
    setIsViewDrawerVisible(true);
  };
  
  const handleAddNew = () => {
    setSelectedInput(null);
    setIsDrawerVisible(true);
  };
  
  const handleImport = () => {
    setIsImportModalVisible(true);
  };

  const handleImportMappings = () => {
    setIsImportMappingsVisible(true);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Intervention Inputs</h2>
          <p className="text-muted-foreground">
            Manage emission factors for different intervention inputs
          </p>
        </div>
        
        {hasEditPermission && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={handleImport}
            >
              <FileUpIcon className="h-4 w-4" />
              Import
            </Button>
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={handleImportMappings}
            >
              <FileUpIcon className="h-4 w-4" />
              Import Mappings
            </Button>
            <Button 
              className="flex items-center gap-2"
              onClick={handleAddNew}
            >
              <PlusIcon className="h-4 w-4" />
              Add New
            </Button>
          </div>
        )}
      </div>

      {/* Table Component */}
      <InterventionInputTable onRowClick={handleRowClick} />

      {/* Details Dialog */}
      <Dialog 
        open={isViewDrawerVisible} 
        onOpenChange={setIsViewDrawerVisible}
      >
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Input Details</DialogTitle>
            <DialogDescription>
              Detailed information about the selected intervention input.
            </DialogDescription>
          </DialogHeader>
          <ViewInterventionInput
            input={selectedInput}
            hasEditPermission={hasEditPermission}
            onEdit={() => {
              setIsViewDrawerVisible(false);
              setIsDrawerVisible(true);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add/Edit Form Dialog */}
      <Dialog 
        open={isDrawerVisible} 
        onOpenChange={setIsDrawerVisible}
      >
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>
              {selectedInput ? "Edit Input" : "Add New Input"}
            </DialogTitle>
          </DialogHeader>
          <AddEditInterventionInput
            onSubmit={handleAddEdit}
            onCancel={() => {
              setIsDrawerVisible(false);
              setSelectedInput(null);
            }}
            initialValues={selectedInput}
          />
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog 
        open={isImportModalVisible} 
        onOpenChange={setIsImportModalVisible}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Import Intervention Inputs</DialogTitle>
            <DialogDescription>
              Upload a CSV or JSON file to import intervention inputs.
            </DialogDescription>
          </DialogHeader>
          <InterventionInputImport
            onSuccess={() => {
              setIsImportModalVisible(false);
              refetch();
            }}
            onCancel={() => setIsImportModalVisible(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Import Mappings Dialog */}
      <Dialog 
        open={isImportMappingsVisible} 
        onOpenChange={setIsImportMappingsVisible}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Import Customer Code Mappings</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import customer code mappings.
            </DialogDescription>
          </DialogHeader>
          <CustomerCodeMappingImport
            onSuccess={() => {
              setIsImportMappingsVisible(false);
            }}
            onCancel={() => setIsImportMappingsVisible(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InterventionInputs;